import { PlusCircleTwoTone } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, List, message, Modal, Row } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, DatePicker, SearchInput } from '../../../components';
import { FIRST_TOUCH_STATUSES, ROLES } from '../../../constants';
import { getProfile } from '../../../utils/auth';
import { withRouter } from '../../../utils/wrappers';
import { getClientDetail } from '../client-details.action';
import { clientService } from '../client-details.service';

const StyledModal = styled.div`
  em.label {
    font-family: AvenirLTStdHeavy;
    font-size: 14px;
    color: #707070;
    display: block;
    font-style: normal;
    margin-bottom: 5px;
  }
`;

const FirstTouchStatuses = ({ params, mappedStatuses = [] }) => {
  const dispatch = useDispatch();
  const { clientID } = params;
  const { roles } = getProfile();
  const isAdmin = roles.includes(ROLES.FINANCE);
  const [status, setStatus] = useState(undefined);
  const [startDate, setStartDate] = useState(undefined);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [viewAllStatuses, setViewAllStatuses] = useState(false);
  const [latestStartDate, setLatestStartDate] = useState(moment().subtract(1, 'days'));

  const disabledDate = (current) => {
    if (current > latestStartDate) {
      return current && current < moment().subtract(1, 'days').endOf('day');
    }

    return current && current < latestStartDate.endOf('day');
  };

  const handleStatus = (value) => {
    setStatus(value);
  };

  const handleStartDate = (value) => {
    setStartDate(value);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleAdd = () => {
    const key = `first-touch-status-${new Date().getTime()}`;
    message.loading({ content: 'Processing...', key });
    setLoading(true);

    if (!status || !startDate) {
      setLoading(false);
      message.warn({ content: 'Please add status and start date', key });
      return;
    }

    const body = {
      first_touch_status: { status, start_date: startDate.format('YYYY-MM-DDT00:00:00') + 'Z' }
    };

    clientService
      .editClientConfig(clientID, body)
      .then(() => {
        setLoading(false);
        setVisible(false);
        message.success({ content: 'Added!', key });
        dispatch(getClientDetail(clientID));
      })
      .catch((err) => {
        setLoading(false);
        message.error({ content: 'Failed to process', key });
        console.error(err);
      });
  };

  const afterClose = () => {
    setStatus(undefined);
    setStartDate(undefined);
  };

  useEffect(() => {
    if (mappedStatuses.length) {
      setStatuses(mappedStatuses.slice(0, 5));
      setLatestStartDate(moment.utc(mappedStatuses[0].start_date));
    }
  }, [mappedStatuses]);

  useEffect(() => {
    if (viewAllStatuses) {
      setStatuses(mappedStatuses);
    } else {
      setStatuses(mappedStatuses.slice(0, 5));
    }
  }, [viewAllStatuses]);

  return (
    <>
      <Modal
        title="Add First Touch Status"
        visible={visible}
        onOk={handleAdd}
        onCancel={handleCancel}
        destroyOnClose={true}
        afterClose={afterClose}
        footer={[
          <Button key="back" type="secondary" disabled={loading} onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" loading={loading} onClick={handleAdd}>
            Add
          </Button>
        ]}
      >
        <StyledModal>
          <em className="label">Billable status</em>
          <SearchInput
            placeholder="Type or select..."
            id="first-touch-status"
            items={FIRST_TOUCH_STATUSES}
            value={status}
            onChange={handleStatus}
          />
          <br />
          <em className="label">Start Date</em>
          <DatePicker
            id="start_date"
            value={startDate}
            onChange={handleStartDate}
            disabledDate={disabledDate}
          />
        </StyledModal>
      </Modal>

      <Row gutter={[24, 16]}>
        <Col span={24}>
          <h5>
            Billable Status &nbsp;
            {isAdmin ? (
              <Button
                type="link"
                icon={<PlusCircleTwoTone twoToneColor="#5050f9" />}
                onClick={() => setVisible(true)}
              />
            ) : (
              <div />
            )}
          </h5>
          <div>
            <List
              header={
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <span className="header">Status</span>
                  </Col>
                  <Col span={6}>
                    <span className="header">Start Date</span>
                  </Col>
                  <Col span={6}>
                    <span className="header">End Date</span>
                  </Col>
                </Row>
              }
              footer={
                (statuses.length !== mappedStatuses.length || mappedStatuses.length > 5) && (
                  <div style={{ textAlign: 'center' }}>
                    <>
                      {!viewAllStatuses && (
                        <span className="view-link" onClick={() => setViewAllStatuses(true)}>
                          View All
                        </span>
                      )}
                      {viewAllStatuses && (
                        <span className="view-link" onClick={() => setViewAllStatuses(false)}>
                          View Less
                        </span>
                      )}
                    </>
                  </div>
                )
              }
              bordered
              size="small"
              dataSource={statuses}
              renderItem={(item) => (
                <Row gutter={[24, 16]} style={{ padding: '8px 15px' }}>
                  <Col span={12}>
                    <span className="value">{item.status}</span>
                  </Col>
                  <Col span={6}>
                    <span className="value">
                      {item.start_date ? moment.utc(item.start_date).format('DD MMM YYYY') : '-'}
                    </span>
                  </Col>
                  <Col span={6}>
                    <span className="value">
                      {item.end_date ? moment.utc(item.end_date).format('DD MMM YYYY') : '-'}
                    </span>
                  </Col>
                </Row>
              )}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default withRouter(FirstTouchStatuses);
